import { Injectable } from '@angular/core';
import { GridsterConfig, GridType} from 'angular-gridster2';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import CookieService from './user/cookie.service';

export type TBreakpoint = "xs" | "sm" | "md" | "lg" | "xl";

export enum EUILanguage {
	English = "English",
	Espanol = "Espanol"
}

export interface IJSScripts {
	name: string;
	src: string;
}

export interface IAppConfig {
	WebAPIUrl: string,
	JourneyAPIUrl: string,
	GoogleMapJSAPI: string,
	GoogleMapId: string,
	WWWJourneysUrl: string,
	RemoteAddr: string,
	InMaintenance: boolean,
	AutoCalcuateSkillMatch: boolean
}

@Injectable({
  providedIn: 'root'
})
export class AppSettingService {
	static appConfig: IAppConfig = {
		WebAPIUrl: "/WEBAPI/",
		JourneyAPIUrl: "/API/",
		GoogleMapJSAPI: "",
		GoogleMapId: "",
		WWWJourneysUrl: "",
		RemoteAddr: "",
		InMaintenance: false,
		AutoCalcuateSkillMatch: false
	};

	// isMobile, isPhone will be auto updated during window resize
	isMobile: boolean = AppSettingService.widgetMobile();
	isPhone: boolean = AppSettingService.widgetPhone();

	constructor() {
		window.addEventListener('resize', () => {
			this.isMobile = AppSettingService.widgetMobile();
			this.isPhone = AppSettingService.widgetPhone();
		});
	}

	static readonly mobileBreakPoint: number = 991;  //size below bootstrap lg
	static readonly phoneBreakPoint: number = 575;   //size below bootstrap sm
	static readonly errorMessage: string = 'No results? Don\'t worry, the Journeys team is actively hunting down this information. We\'ve got your back.';
	static readonly noDataMessage: string = 'Sometimes even Journeys can\'t find something. Don\'t worry, we\'ll keep our eyes out for this information.';
	static readonly journeyUI: string = "/journey/index.html#";
	static readonly baseUrl = "/UI";
	static readonly obsoleteSOC: string = "Due to updates/changes by the Department of Labor Career, the requested page is no longer available.";
	static readonly invalidPage: string = "The requested page is no longer available.";

	static widgetMobile(): boolean {
		return (window.innerWidth <= this.mobileBreakPoint) ? true : false;
	}

	static widgetPhone(): boolean {
		return (window.innerWidth <= this.phoneBreakPoint) ? true : false;
	}

	
	static getBreakpoint(): TBreakpoint {
		if (window.innerWidth < 576) { return "xs"; }
		else if (window.innerWidth < 768) { return "sm"; }
		else if (window.innerWidth < 992) { return "md"; }
		else if (window.innerWidth < 1200) { return "lg"; }
		else { return "xl"; }
	}

	static showToolTip(): boolean {
		return (window.innerWidth < 1460) ? true : false;
	}

	static gridsterOptions: GridsterConfig = {
		columns: 12,
		margins: 10,
		mobileBreakpoint: AppSettingService.mobileBreakPoint,
		gridType: GridType.ScrollVertical, // will fit on width and height of the items will be the same as the width
		minCols: 12,
		maxCols: 12,
		setGridSize: true,  // set the height in gridster element, disable this and use style to control
		draggable: {enabled: false},
		resizable: {enabled: false}
	};

	static idle = {
		timeoutAfter: 600,
		warningDuration: 60,
		keepAlive: 30
	}; //idle: 10 minutes idle warning 30 seconds

	static objectChildrenLabel(territoryId: number, levelId: string): string {
		if (territoryId === 1) {
			let children = (levelId === "1.1") ? "Clusters" : (levelId === "1.2") ? "Pathways" : "Occupations";
			return children;
		}
		//Vocational/Certification
		else if (territoryId === 2) {
			return "";
		}
		//CTE
		else if (territoryId === 3) {
			let children = (levelId === "3.1") ? "Clusters" : (levelId === "3.2") ? "Pathways" : (levelId === "3.3") ? "Strands" : (levelId === "3.4") ? "Standards" : "Performance Indicator";
			return children;
		}
		//Militaty
		else if (territoryId === 4) {
			return "";
		}
		//College
		else if (territoryId === 5) {
			let children = (levelId === "5.1") ? "Clusters" : (levelId === "5.2") ? "Programs" : "Courses";
			return children;
		}
		//K12
		else if (territoryId === 6) {
			let children = (levelId === "6.1") ? "Grades" : (levelId === "6.2") ? "Subjects" : (levelId === "6.3") ? "Standards" : (levelId === "6.4") ? "Strands" : (levelId === "6.5") ? "Benchmark" : "Sub_benchmark";
			return children;
		}
		//PreK
		else if (territoryId === 7) {
			return "";
		}
		else {
			return "";
		}
	};
};


@Injectable({
	providedIn: 'root'
})
export class AppUtilityService {
	private static numberRegExp = /[0-9]/;
	private static upperRegExp = /[A-Z]/;

	// return 3133.3343 t o 3,33.3343
	static toCommaNumber(v: any): string {
		v = (v??"") + "";
		v = v.replace(/^\s+|\s+$/g, '');
		if (!isNaN(v)) {
			var whole = v.indexOf(".") >= 0 ? v.split(".")[0] : v;
			var fraction = v.indexOf(".") >= 0 ? v.split(".")[1] : "";
			return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (fraction == "" ? "" : "." + fraction);
		}
		else {
			return v;
		}
	}

	// r is decimal place, if r is NaN, then the return will be integer
	static toRoundNumber(v: any, r: any): string {
		v = (v ?? "") + "";
		v = v.replace(/^\s+|\s+$/g, '');
		r = r + "";
		r = r.replace(/^\s+|\s+$/g, '');
		if (!isNaN(v)) {
			if (isNaN(r)) { r = 0; }
			else if (r.indexOf("-") >= 0) { r = r.replace("-", ""); }
			return parseFloat(v).toFixed(parseInt(r));
		}
		else {
			return v;
		}
	}

	// convert to dollar, when toEven is true, then return 343.5 to $344, 343.4 to $343; when false, then return 234.332332 to $234.33
	static toDollar(v: any, toEven: any): string {
		v = (v ?? "") + "";
		v = v.replace(/^\s+|\s+$/g, "");
		var nv = v.replace("$", "");
		if (nv === "") {
			return "N/A";
		}
		toEven = toEven + "";
		toEven = toEven.toLowerCase() == "true" ? true : false;
		if (nv == "" || isNaN(nv)) {
			return v;
		}
		else {
			return "$" + this.toCommaNumber(this.toRoundNumber(nv, (toEven ? 0 : -2)));
		}
	}

	// convert data from 0.234 to 23%, 0.235 to 24$. Use r to specify decimal place
	static toPercent(v: any, r: any): string {
		v = (v ?? "") + "";
		v = v.replace(/^\s+|\s+$/g, "");
		if (v == "" || isNaN(v) || parseFloat(v) > 1 || parseFloat(v) < 1) {
			return v;
		}
		else {
			r = r + "";
			r = r.replace(/^\s+|\s+$/g, '');
			if (isNaN(r) || parseFloat(r) < 0) { r = 0; }
			return (this.toRoundNumber(parseFloat(v) * 100, r)) + "%";
		}
	}

	// convert true or false to yes or no, if no value or null, return N/A
	static toYesNo(v: any): string {
		if ((v ?? "") == "") {
			return 'N/A';
		}
		else {
			return (v === true || v.toLowerCase() === 'true') ? 'Yes' : 'No';
		}
	}

	//return date in format of 01/01/2000
	static toDate(v: string):string {
		if ((v ?? "") == '') {
			return '';
		}
		else {
			try {
				var x = new Date(v.indexOf('T') > 0 ? v : v + 'T00:00:00Z');
				return ('0' + (x.getUTCMonth() + 1)).slice(-2) + '/' + ('0' + x.getUTCDate()).slice(-2) + '/' + x.getUTCFullYear();
			}
			catch (ex) { return ''; }
		}
	}

	static toDate2Year(v: string):string {
		if ((v ?? "")  == '') {
			return '';
		}
		else {
			try {
				var x = this.toDate(v);
				return x.split('/')[0] + '/' + x.split('/')[1] + '/' + x.split('/')[2].slice(-2);
			}
			catch (ex) { return ''; }
		}
	}

	//password validator returns message and password strength
	static validatePassword(pass: string): any {
		var strength = 0;
		var msg = '';
		var passMinLen = 6;
		if (this.findUppercase(pass) > 0) {
			strength += 40;
		}
		else if (this.findNumeric(pass) > 0) {
			strength += 40;
		}
		else {
			msg = 'Password length must be at least 6 characters and include 1 upper case or 1 numeric value.';
		}
		for (var i = 2; i <= passMinLen; i += 2) {
			if (pass.length >= i) {
				strength += 20;
			}
		}
		if (msg.length === 0 && pass.length < passMinLen) {
			msg = 'Use at least 6 characters';
		}
		return { 'message': msg, 'strength': strength };
	}

	static findUppercase(pass: string): number {
		return this.findRepExp('uppercase', pass);
	}

	static findNumeric(pass: string): number {
		return this.findRepExp('number', pass);
	}

	private static findRepExp(type: string, pass: string):number {
		var regex = this.upperRegExp;
		if (type === 'number') {
			regex = this.numberRegExp;
		}
		let count: number = 0, len: number  = pass.length;
		for (var i = 0; i < len; i++) {
			if (regex.test(pass.charAt(i))) count++;
		}
		return count;
	}

	static isEmail(str: string): boolean {
		var pattern = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
		return pattern.test(str);  // returns a boolean 
	}

	// generate a new guid id
	static newId(): string {
		function s4() {
			return Math.floor((1 + Math.random()) * 0x10000)
				.toString(16)
				.substring(1);
		}
		return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4();
	}

	// check if the display style value
	static isVisible(el: any):boolean {
		if (el.nativeElement) {
			return (el.nativeElement.offsetParent != null);
		}
		else {
			try {
				return (el.offsetParent != null);
			}
			catch (ex) {
				return true;
			}
		}
	}

	// check if the element is visible on current screen 
	static isVisibleOnScreen(el: any): boolean {
		var visible = false;
		var e: any;
		if (el.nativeElement) {
			visible = (el.nativeElement.offsetParent != null);
			e = el.nativeElement;
		}
		else {
			try {
				visible = (el.offsetParent != null);
				e = el;
			}
			catch (ex) {
				return true;
			}
		}
		// if visible, then check if it is within the scroll range
		if (visible) {
			try {
				let recct:any = e.getBoundingClientRect();
				let visible: boolean = (recct.top >= 0) && ((recct.top <= window.innerHeight) || (recct.bottom <= window.innerHeight));
				return visible;
			}
			catch (ex) {
				return false;
			}
		}
		else {
			return visible;
		}
	};

	static toPhoneNumber(tel: string): string {
		let value = tel.toString().trim().replace(/^\+|-|\(|\)/g, '');
		let country: string;
		let city: string;
		let number: string;

		if (value.match(/[^0-9]/)) {
			return tel;
		}

		switch (value.length) {
			case 10: // +1PPP####### -> C (PPP) ###-####
				country = "1";
				city = value.slice(0, 3);
				number = value.slice(3);
				break;
			case 11: // +CPPP####### -> CCC (PP) ###-####
				country = value[0];
				city = value.slice(1, 4);
				number = value.slice(4);
				break;
			case 12: // +CCCPP####### -> CCC (PP) ###-####
				country = value.slice(0, 3);
				city = value.slice(3, 5);
				number = value.slice(5);
				break;
			default:
				return tel;
		}
		if (country == "1") {
			country = "";
		}
		number = number.slice(0, 3) + "-" + number.slice(3);
		return (country + " (" + city + ") " + number).trim();
	}

	static isGuid(value: any):boolean {
		let regex = /[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}/i;
		let match = regex.exec(value);
		return match != null;
	}

	static isZipCode(zip: string): boolean {
		return /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(zip);
	}

	static openInNewTab(url: string, tabName:string = null): void {
		if (url.trim().length == 0) {
			return;
		}

		url = (url.indexOf("http") < 0 ? "https://" : "") + url.trim();
		if (tabName == null) {
			const newTab = window.open(url, '_blank', 'noopener,noreferrer')
			if (newTab) {
				newTab.opener = null
			}
		}
		else {
			const oldTab = window.open(url, tabName);
			if (oldTab) {
				oldTab.focus();
			}
		}
	}

	static openMainDashboard(url: string): void {
		if (url.trim().length == 0) {
			return;
		}

		url = (url.indexOf("http") < 0 ? "https://" : "") + url.trim();
		const tab = window.open(url, "MainDashboard");
		if (tab) {
			tab.focus();
		}
	}

	static copyObject(source: any): any {
		return (JSON.parse(JSON.stringify(source)));
	}

	// convert object to formdata
	static objectToFormData(obj: any, form?: FormData, namespace?: string): FormData {
		const formData = form || new FormData();

		for (let property in obj) {
			if (!obj.hasOwnProperty(property)) continue;

			const formKey = namespace ? `${namespace}[${property}]` : property;

			if (obj[property] == null || obj[property] == undefined) {
				continue;
			}
			else if (obj[property] instanceof Date) {
				formData.append(formKey, obj[property].toISOString());
			}
			else if (obj[property] instanceof File) {
				formData.append(formKey, obj[property]);
			}
			else if (typeof obj[property] === 'object' && !(obj[property] instanceof File)) {
				this.objectToFormData(obj[property], formData, formKey);
			}
			else {
				formData.append(formKey, obj[property].toString());
			}
		}

		return formData;
	}

	static isDate(value: string): boolean {
		var date = Date.parse(value);
		if (isNaN(date))
			return false;
		else
			return true;
	}

	private static dateFormatter: any = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
	// formate date to Dec 12, 2012
	static  formatDate(date: string): string {
		if (date.trim().length == 0) {
			return "";
		}
		else {
			return this.dateFormatter.format(new Date(date));
		}
	}

	static goMarketingSite(replace: boolean = true) {
		if (replace) {
			window.location.replace(AppSettingService.appConfig.WWWJourneysUrl);
		}
		else {
			window.location.href = AppSettingService.appConfig.WWWJourneysUrl;
		}
	}
}


@Injectable({
	providedIn: 'root'
})
export class AppService {
	fromEmbedOccupations: boolean = false;
	private validRouteRegices;

	constructor(
		private http: HttpClient,
		private router: Router
	) {
		const validRoutes: any[] = [];

		// router.config will not change so lets cache
		// get all routes and child routes
		this.router.config.forEach((route) => {
			const routePath: string = route.path
			validRoutes.push(routePath)
			const routeChildren = route.children || []
			routeChildren.forEach((routeChild) => {
				const routeChildPath: string = route.path + '/' + routeChild.path
				validRoutes.push(routeChildPath)
			})
		});
		// swap routes for regices to support URL params and tidy up a little
		this.validRouteRegices = validRoutes.map((route) => route.startsWith('/') ? route.replace('/', '') : route)
			.map((route) => route.replace(/\/:[a-zA-Z]+/g, '/[a-zA-Z0-9]+'))
			.filter((route) => route !== '' && route !== '**')
			.map((route) =>  route);
	}

	// call this to check if a route exists or not
	isRouteValid(pathname = location.pathname): boolean {
		const locationPathname = pathname.startsWith('/') ? pathname.replace('/', '') : pathname
		let xx = this.validRouteRegices.filter(x => locationPathname.indexOf(x) === 0);
		if (xx.length > 0) {
			return true;
		}
		else {
			return false;
		}
	}

	//check if we have account cookie
	checkAccountCookie() {
		let account = CookieService.getCookie("account");
		return new Promise((resolve, reject) => {
			if (account == null) {
				this.http.get<any>(AppSettingService.appConfig.JourneyAPIUrl + "Session/Anonymous")
					.subscribe({
						next: () => {
							resolve(true);
						},
						error: (err: any) => {
							console.log(err);
							reject(err);
						}
					});
				}
			else {
				resolve(true);
			}
		});
	}

	// redirect and reload
	redirectReload(uri: string, params: any = null) {
		this.router.navigateByUrl('/Splash', { skipLocationChange: true }).then(() => {
			if (params == null) {
				this.router.navigate([uri]);
			}
			else {
				this.router.navigate([uri], params);
			}
		});
	}

	private JSScripts: IJSScripts[] = [];
	loadJSScript(js: IJSScripts): any {
		return new Promise((resolve, reject) => {
			//resolve if already loaded
			let ej = this.JSScripts.filter(v => v.name === js.name);
			if (ej != null && ej.length > 0) {
				resolve({ jsloaded: true });
			}
			else {
				// remove from array if exist
				if (ej != null) {
					this.JSScripts = this.JSScripts.filter(obj => obj !== js);
				}
				//load script
				let script = document.createElement('script');
				script.setAttribute('type', 'text/javascript');
				script.setAttribute('src', js.src);
				script.setAttribute('async', 'true');
				document.head.appendChild(script);
				script.onerror = (err: any) => {
					console.log(err);
					reject({ jsloaded: false });
				};
				script.onload = () => {
					this.JSScripts.push(js);;
					resolve({ jsloaded: true });
				};
			}
		});
	}

	// determine if the url is to new ui, old ui, or external
	goTo(url: string): void {
		url = (url ?? "").trim();
		if (url === "") {
			return;
		}
		else if (url.toLowerCase().indexOf("http") == 0) {
			window.location.href = url;
		}
		else {
			if (url.indexOf(AppSettingService.baseUrl) === 0) {
				url = url.replace(AppSettingService.baseUrl  + "/", "/");
			}
			if (this.isRouteValid(url)) {
				this.router.navigateByUrl(url);
			}
			else {
				//url to old ui
				if (url.toLocaleLowerCase().indexOf(AppSettingService.journeyUI.toLocaleLowerCase()) < 0) {
					location.href = AppSettingService.journeyUI + url;
				}
				else {
					location.href = url;
				}
			}
		}
	}
}
